import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Icons } from "assets";
import React, { useEffect, useState } from "react";
import Loader from "shared/components/loader/animation";
import Pagination from "shared/components/pagination";
import useWindowSize from "shared/customHooks/windowSize";
import styles from "../style.module.scss";

import EditIcon from "@mui/icons-material/Edit";
import FlashOffIcon from "@mui/icons-material/FlashOff";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import CustomButton from "shared/components/customButton";
import ItemsPerPage from "shared/components/itemsPerPage";
import { toastMessage } from "shared/components/toast";
import useDebounce from "shared/customHooks/useDebounce";
import Confirmation from "shared/modals/confirmation";
import {
  activateGenreService,
  allGenreService,
  deleteGenresService,
  inActivateGenreService,
} from "shared/services/productManagerService";
import AddGenre from "./AddGenre";
import SkeletonLoader from "./skeletonLoader";

function GenresTab() {
  const screenWidth = useWindowSize();
  const [activePage, setActivePage] = useState<number>(1);
  const [open, setOpen] = useState<boolean>(false);
  const [allGenres, setAllGenres] = useState<any>(null);
  const [search, setSearch] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [totalPage, setTotalPage] = useState<number>(0);
  // Starts true: getAllGenres() runs from a useEffect, which fires only after
  // the first render commits. Starting false paints "No Data Found" for a
  // frame (the list is still empty) before the skeleton ever appears.
  const [loader, setLoader] = useState<boolean>(true);
  const [openConfirm, setOpenConfirmation] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [actionLoader, setActionLoader] = useState<boolean>(false);
  const [action, setAction] = useState<any>({
    label: "",
    type: "",
  });
  const [totalItems, setTotalItems] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const getAllGenres = () => {
    setLoader(true);
    allGenreService(searchText, activePage, null, itemsPerPage)
      .then(({ data }) => {
        setAllGenres(data?.data?.data);
        setTotalPage(data?.data?.last_page);
        setLoader(false);
        setItemsPerPage(data?.data?.per_page);
        setCurrentPage(data?.data?.current_page);
        setTotalItems(data?.data?.total);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        console.log("error", err?.response?.data?.message);
        setLoader(false);
      });
  };

  useEffect(() => {
    getAllGenres();
    // eslint-disable-next-line
  }, [searchText, activePage, itemsPerPage, currentPage]);

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      // Nothing changed once trimmed (e.g. only spaces were typed) — keep the
      // current page and skip the request entirely.
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800
  );

  const handleOpenConfirmation = (label: String, type: String, item: any) => {
    setOpenConfirmation(true);
    setAction({
      label: label,
      type: type,
    });
    setSelectedItem(item);
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
    setAction({
      label: "",
      type: "",
    });
    setSelectedItem(null);
  };

  const handleActions = (action: any) => {
    setActionLoader(true);
    let service =
      action.type === "delete"
        ? deleteGenresService
        : action.type === "active"
        ? activateGenreService
        : action.type === "inactive"
        ? inActivateGenreService
        : null;

    service?.(selectedItem?.id)
      .then(({ data }) => {
        toastMessage("success", data?.message);
        setActionLoader(false);
        getAllGenres();
        setSelectedItem(null);
        handleCloseConfirmation();
        setSelectedItem(null);
      })
      .catch((err) => {
        setActionLoader(false);
        toastMessage("error", err?.response?.data?.message);
        setSelectedItem(null);
      });
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handleOpen = (item?: any) => {
    setOpen(true);
    if (item) {
      setSelectedItem(item);
    }
  };

  return (
    <Box className={styles.topLevelContainer}>
      <Box className={styles.topHeadContainer}>
        <Box>
          <div className={styles.tableHeading}>Books Catalog.</div>
          <div className={styles.tableSubHeading}>
            All genre are showing here.
          </div>
        </Box>
        <Box className={styles.filterContainer}>
          <div className={styles.gridContainer}>
            <CustomButton
              text={"Add New"}
              buttonStyle={styles.addBtn}
              handleClick={() => handleOpen()}
            />
          </div>
          <div className={styles.gridContainer}>
            <Icons.SearchIcon className={styles.searchIcon} />
            <input
              placeholder="Search"
              className={styles.input}
              value={search}
              onChange={(e: any) => setSearch(e.target.value)}
            />
          </div>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        className={styles.table}
        sx={screenWidth > 990 ? { width: `${screenWidth - 300}px` } : {}}
      >
        <Table aria-label="simple table">
          <TableHead>
            <TableRow className={styles.tableHead}>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Total Books</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loader ? (
              <SkeletonLoader />
            ) : (
              allGenres?.map((item: any) => {
                return (
                  <>
                    <TableRow className={styles.tableRow}>
                      <TableCell>
                        <Box display={"flex"} alignItems={"center"}>
                          {item?.icon && (
                            <img
                              src={item?.icon}
                              alt="book cover"
                              className={styles.bookCover}
                            />
                          )}
                          <span style={{ marginLeft: "10px" }}>
                            {item?.name}
                          </span>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`${
                            item?.is_active
                              ? styles.status_active
                              : styles.status_inactive
                          }`}
                        >
                          {item?.is_active ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item?.books_count ? (
                          <Tooltip
                            arrow
                            title={
                              <Box sx={{ p: 0.5, fontSize: "12px" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 2,
                                  }}
                                >
                                  <span>Pending:</span>
                                  <span>{item.books_count.pending}</span>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 2,
                                  }}
                                >
                                  <span>In Review:</span>
                                  <span>{item.books_count.in_review}</span>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 2,
                                  }}
                                >
                                  <span>Revision Requested:</span>
                                  <span>
                                    {item.books_count.revision_requested}
                                  </span>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 2,
                                  }}
                                >
                                  <span>Revision Submitted:</span>
                                  <span>
                                    {item.books_count.revision_submitted}
                                  </span>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 2,
                                  }}
                                >
                                  <span>Approved:</span>
                                  <span>{item.books_count.approved}</span>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 2,
                                  }}
                                >
                                  <span>Published:</span>
                                  <span>{item.books_count.published}</span>
                                </Box>
                              </Box>
                            }
                          >
                            <span style={{ cursor: "help" }}>
                              {item.books_count.total}
                            </span>
                          </Tooltip>
                        ) : (
                          "Not Provided"
                        )}
                      </TableCell>
                      <TableCell>
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          className={styles.actionBtn}
                        >
                          <Tooltip
                            arrow
                            title={
                              item?.is_active ? "Activate" : "Deactivate"
                            }
                          >
                            <button
                              onClick={() =>
                                handleOpenConfirmation(
                                  `Are you sure you want to ${
                                    item?.is_active === 1
                                      ? "deactivate"
                                      : "activate"
                                  } this catalog?`,
                                  `${
                                    item?.is_active === 1
                                      ? "inactive"
                                      : item?.is_active === 0
                                      ? "active"
                                      : ""
                                  }`,
                                  item
                                )
                              }
                            >
                              {item?.is_active ? (
                                <FlashOnIcon />
                              ) : (
                                <FlashOffIcon />
                              )}
                            </button>
                          </Tooltip>
                          <Tooltip arrow title={"Edit"}>
                            <button onClick={() => handleOpen(item)}>
                              <EditIcon />
                            </button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!(allGenres?.length > 0) && !loader && (
        <div className={styles.noUserCard}>
          <Icons.NoDataIcon />
          <h5>No Data Found</h5>
        </div>
      )}
      {totalPage > 1 && allGenres.length > 0 && (
        <Pagination
          totalPages={totalPage}
          activePage={activePage}
          setActivePage={setActivePage}
        />
      )}
      <Box>
        {allGenres?.length > 1 ? (
          <ItemsPerPage
            setActivePage={setActivePage}
            currentPage={currentPage}
            setItemsPerPage={setItemsPerPage}
            itemsPerPage={itemsPerPage ? itemsPerPage : 10}
            totalItems={totalItems}
          />
        ) : null}
      </Box>
      <Confirmation
        open={openConfirm}
        handleClose={() => handleCloseConfirmation()}
        handleClick={() => handleActions(action)}
        text={action.label}
      />

      {open && (
        <AddGenre
          open={open}
          handleClose={handleClose}
          selectedCategory={selectedItem}
          getAllGenres={getAllGenres}
        />
      )}

      {actionLoader && <Loader />}
    </Box>
  );
}

export default GenresTab;
